import { useState } from "react";
import { format } from "date-fns";
import { MoreHorizontal } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useAmendmentsForInstruction,
  useSowingsForInstruction,
} from "../../hooks/useSowings";
import {
  AMENDMENT_ACTION,
  type Sowing,
  type SowingAmendment,
} from "../../services/sowingsApi";
import { AmendSowingDialog } from "./AmendSowingDialog";
import { WithdrawSowingDialog } from "./WithdrawSowingDialog";
import { ReparentSowingDialog } from "./ReparentSowingDialog";
import { useAuthStore } from "@/features/auth/stores/authStore";

interface SowingsPanelProps {
  sowingInstructionId: string;
}

type ActionType = "amend" | "withdraw" | "reparent" | null;

const formatDate = (iso?: string | null) => {
  if (!iso) return "—";
  try {
    return format(new Date(iso), "yyyy-MM-dd");
  } catch {
    return iso;
  }
};

const formatDateTime = (iso?: string | null) => {
  if (!iso) return "—";
  try {
    return format(new Date(iso), "yyyy-MM-dd HH:mm");
  } catch {
    return iso;
  }
};

const ACTION_LABEL: Record<number, string> = {
  [AMENDMENT_ACTION.FieldEdit]: "Field edit",
  [AMENDMENT_ACTION.Withdraw]: "Withdraw",
  [AMENDMENT_ACTION.Reparent]: "Re-parent",
};

const ACTION_VARIANT: Record<
  number,
  "default" | "secondary" | "destructive" | "outline"
> = {
  [AMENDMENT_ACTION.FieldEdit]: "secondary",
  [AMENDMENT_ACTION.Withdraw]: "destructive",
  [AMENDMENT_ACTION.Reparent]: "outline",
};

const tryParseSnapshot = (
  raw: string | null | undefined
): Record<string, unknown> | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

const renderDiff = (a: SowingAmendment) => {
  if (a.action === AMENDMENT_ACTION.Withdraw) {
    return (
      <span className="text-muted-foreground italic">
        Row hard-deleted; no longer in live queries.
      </span>
    );
  }
  if (a.action === AMENDMENT_ACTION.Reparent) {
    const from = a.originalSowingInstructionId?.slice(0, 8) ?? "?";
    const to = a.newSowingInstructionId?.slice(0, 8) ?? "?";
    return (
      <span>
        Moved from instruction <code>{from}</code> to <code>{to}</code>.
      </span>
    );
  }
  const prior = tryParseSnapshot(a.priorSnapshot);
  const next = tryParseSnapshot(a.newSnapshot);
  if (!prior || !next) return <span className="text-muted-foreground">—</span>;

  const fields: Array<keyof typeof prior> = [
    "TraysSown",
    "SowingDate",
    "GerminationChamberId",
    "Notes",
  ];
  const changed = fields
    .filter((f) => JSON.stringify(prior[f]) !== JSON.stringify(next[f]))
    .map((f) => (
      <li key={String(f)} className="text-xs">
        <span className="font-medium">{String(f)}</span>:{" "}
        <span className="text-muted-foreground">{String(prior[f] ?? "—")}</span>
        {" → "}
        <span>{String(next[f] ?? "—")}</span>
      </li>
    ));
  if (changed.length === 0) {
    return <span className="text-muted-foreground">No field changes.</span>;
  }
  return <ul className="space-y-0.5">{changed}</ul>;
};

export const SowingsPanel = ({ sowingInstructionId }: SowingsPanelProps) => {
  const sowingsQuery = useSowingsForInstruction(sowingInstructionId);
  const historyQuery = useAmendmentsForInstruction(sowingInstructionId);

  const { hasPermission } = useAuthStore();
  const canAmend = hasPermission("sowings.amend");
  const canWithdraw = hasPermission("sowings.withdraw");
  const canReparent = hasPermission("sowings.reparent");
  const hasAnyAction = canAmend || canWithdraw || canReparent;

  const [activeRow, setActiveRow] = useState<Sowing | null>(null);
  const [action, setAction] = useState<ActionType>(null);

  const handleClose = () => {
    setAction(null);
    setActiveRow(null);
  };

  return (
    <Tabs defaultValue="sowings" className="w-full">
      <TabsList>
        <TabsTrigger value="sowings">
          Sowings ({sowingsQuery.data?.length ?? 0})
        </TabsTrigger>
        <TabsTrigger value="history">
          Change history ({historyQuery.data?.length ?? 0})
        </TabsTrigger>
      </TabsList>

      <TabsContent value="sowings" className="mt-4">
        {sowingsQuery.isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : sowingsQuery.data && sowingsQuery.data.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sowing date</TableHead>
                <TableHead>Trays sown</TableHead>
                <TableHead>Chamber</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sowingsQuery.data.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{formatDate(s.sowingDate)}</TableCell>
                  <TableCell>{s.traysSown}</TableCell>
                  <TableCell>
                    {s.germinationChamberId
                      ? s.germinationChamberId.slice(0, 8)
                      : "—"}
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {s.notes ?? "—"}
                  </TableCell>
                  <TableCell>
                    {hasAnyAction &&
                      (s.hasGermination ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled
                                aria-label="Locked — germination recorded"
                              >
                                <MoreHorizontal className="h-4 w-4 opacity-40" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Locked — a germination wave has been recorded
                              for this sowing. Amendments are no longer allowed.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canAmend && (
                              <DropdownMenuItem
                                onClick={() => {
                                  setActiveRow(s);
                                  setAction("amend");
                                }}
                              >
                                Amend
                              </DropdownMenuItem>
                            )}
                            {canReparent && (
                              <DropdownMenuItem
                                onClick={() => {
                                  setActiveRow(s);
                                  setAction("reparent");
                                }}
                              >
                                Re-parent
                              </DropdownMenuItem>
                            )}
                            {canWithdraw && (
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => {
                                  setActiveRow(s);
                                  setAction("withdraw");
                                }}
                              >
                                Withdraw
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-sm text-muted-foreground">
            No sowings recorded under this instruction yet.
          </div>
        )}
      </TabsContent>

      <TabsContent value="history" className="mt-4">
        {historyQuery.isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : historyQuery.data && historyQuery.data.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>By</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {historyQuery.data.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="whitespace-nowrap">
                    {formatDateTime(a.amendedAt)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={ACTION_VARIANT[a.action]}>
                      {ACTION_LABEL[a.action] ?? a.action}
                    </Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {a.amendedByUserName ?? (
                      <code className="text-xs">
                        {a.amendedByUserId.slice(0, 8)}
                      </code>
                    )}
                  </TableCell>
                  <TableCell className="max-w-xs">{a.reason ?? "—"}</TableCell>
                  <TableCell>{renderDiff(a)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-sm text-muted-foreground">
            No amendments recorded for this instruction.
          </div>
        )}
      </TabsContent>

      {activeRow && action === "amend" && (
        <AmendSowingDialog
          sowing={activeRow}
          open
          onOpenChange={(open) => !open && handleClose()}
          onSuccess={handleClose}
        />
      )}
      {activeRow && action === "withdraw" && (
        <WithdrawSowingDialog
          sowing={activeRow}
          open
          onOpenChange={(open) => !open && handleClose()}
          onSuccess={handleClose}
        />
      )}
      {activeRow && action === "reparent" && (
        <ReparentSowingDialog
          sowing={activeRow}
          open
          onOpenChange={(open) => !open && handleClose()}
          onSuccess={handleClose}
        />
      )}
    </Tabs>
  );
};
